/**
 * DateFilterWithExport - Reusable Date Filter, Filters, and Export Component
 *
 * @param {Object} props
 * @param {moment.Moment} props.startDate - Start date for date range picker
 * @param {moment.Moment} props.endDate - End date for date range picker
 * @param {Function} props.onDateChange - Callback when date range changes
 * @param {Function} props.onExport - Callback when Export button is clicked
 * @param {boolean} props.showExport - Whether to show export button
 * @param {boolean} props.isExporting - Whether export is in progress
 * @param {Array} props.filterFields - Array of filter field configurations
 * @param {Object} props.filterValues - Current filter values object
 * @param {Function} props.onFiltersApply - Callback when filters are applied
 * @param {Function} props.onFiltersClear - Callback when filters are cleared
 * @param {React.ReactNode} props.middleContent - Optional custom content to render in the middle
 */

import React, { useState, useEffect, useRef } from 'react';
import { Box, Badge, Typography } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import DateRangePickerOmni from 'allGlobalComponents/DateRangePickerOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import SearchableDropdown from 'allGlobalComponents/SearchableDropdown';
import CachedIcon from '@mui/icons-material/Cached';
import { useTheme } from '@mui/material/styles';
import ButtonOmniNew from './ButtonOmniNew';

const useStyles = makeStyles(theme => ({
	'@keyframes filterBarIn': {
		'0%': { opacity: 0, transform: 'translateY(10px)' },
		'100%': { opacity: 1, transform: 'translateY(0)' }
	},
	filterSection: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: theme.spacing(2),
		flexWrap: 'wrap',
		gap: theme.spacing(2),
		margin: '0 20px',
		position: 'relative',
		zIndex: 1200,
		animation: '$filterBarIn 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94) backwards'
	},
	filterControls: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
		flexWrap: 'wrap'
	},
	datePickerContainer: {
		position: 'relative',
		zIndex: theme.zIndex.modal + 1
	},
	middleSection: {
		display: 'flex',
		alignItems: 'center',
		flex: 1,
		justifyContent: 'center',
		gap: theme.spacing(2)
	},
	rightSection: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2)
	},
	filterContent: {
		padding: theme.spacing(3),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2.5)
	},
	filterRow: {
		display: 'flex',
		gap: theme.spacing(2),
		flexWrap: 'wrap',
		[theme.breakpoints.down('sm')]: {
			flexDirection: 'column'
		}
	},
	buttonContainer: {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		marginTop: theme.spacing(2),
		gap: theme.spacing(2)
	}
}));

function DateFilterWithExport({
	title = null,
	startDate,
	endDate,
	onDateChange,
	onExport,
	showExport = false,
	isExporting = false,
	showDateRange = true,
	// Filter props
	filterFields = [],
	filterValues = {},
	onFiltersApply,
	onFiltersClear,
	customRightContent = null,
	showRefresh = false,
	onRefresh = null,
	middleContent = null
}) {
	const classes = useStyles();
	const hasFilters = filterFields.length > 0;
	const theme = useTheme();

	// Local state
	const [filterDialogOpen, setFilterDialogOpen] = useState(false);
	const [localFilters, setLocalFilters] = useState({});
	const wasDialogOpen = useRef(false);

	// Seed local filters from the latest values only when the dialog transitions to open.
	// `filterValues` is rebuilt as a new object on every parent render, so reseeding on every
	// change would wipe in-progress selections whenever the parent re-renders (e.g. background
	// polling) while the dialog is open.
	useEffect(() => {
		if (filterDialogOpen && !wasDialogOpen.current) {
			setLocalFilters({ ...filterValues });
		}
		wasDialogOpen.current = filterDialogOpen;
	}, [filterDialogOpen, filterValues]);

	// Calculate active filter count
	const getActiveFilterCount = () => {
		return Object.values(filterValues).filter(val => {
			if (Array.isArray(val)) return val.length > 0 && val.some(v => v !== '');
			return val !== '' && val !== null && val !== undefined;
		}).length;
	};

	// Handle field change
	const handleFieldChange = (fieldName, value) => {
		setLocalFilters(prev => ({ ...prev, [fieldName]: value }));
	};

	// Handle apply filters
	const handleApply = () => {
		onFiltersApply?.(localFilters);
		setFilterDialogOpen(false);
	};

	// Handle clear filters
	const handleClear = () => {
		const emptyFilters = {};
		filterFields.forEach(field => {
			emptyFilters[field.name] = field.defaultValue ?? '';
		});
		setLocalFilters(emptyFilters);
		onFiltersClear?.(emptyFilters);
		setFilterDialogOpen(false);
	};

	// Handle cancel
	const handleCancel = () => {
		setFilterDialogOpen(false);
	};

	// Render filter field based on config
	const renderFilterField = field => {
		const value = localFilters[field.name] ?? '';

		if (field.visibleWhen && !field.visibleWhen(localFilters)) {
			return null;
		}

		// Handle fully custom field — caller controls rendering (e.g. an input + action button).
		// Receives the dialog's current local filters and a setter for convenience.
		if (field.type === 'custom' && typeof field.render === 'function') {
			return <React.Fragment key={field.name}>{field.render(localFilters, handleFieldChange)}</React.Fragment>;
		}

		// Handle searchable dropdown type
		if (field.type === 'searchable') {
			const selectedOption = field.options?.find(opt => opt.value?.toString() === value?.toString()) || null;
			return (
				<SearchableDropdown
					key={field.name}
					label={field.label}
					options={[{ value: '', label: 'All' }, ...(field.options || [])]}
					value={selectedOption}
					onChange={newValue => handleFieldChange(field.name, newValue?.value?.toString() || '')}
					placeholder={field.placeholder || 'Search...'}
					disableSort={field.disableSort}
					style={{ minWidth: '200px', width: '100%' }}
				/>
			);
		}

		return (
			<FormFieldOmni
				key={field.name}
				label={field.label}
				variant="outlined"
				value={value}
				onChange={e => handleFieldChange(field.name, e.target.value)}
				type={field.type || 'text'}
				options={field.options}
				size="small"
				fullWidth
				placeholder={field.placeholder}
				error={field.error}
				helperText={field.helperText}
				{...field.props}
			/>
		);
	};

	// Group fields by row
	const groupFieldsByRow = () => {
		const rows = [];
		let currentRow = [];

		filterFields.forEach((field, index) => {
			if (field.newRow && currentRow.length > 0) {
				rows.push(currentRow);
				currentRow = [];
			}
			currentRow.push(field);
			if (field.fullRow || index === filterFields.length - 1) {
				rows.push(currentRow);
				currentRow = [];
			}
		});

		if (currentRow.length > 0) {
			rows.push(currentRow);
		}

		return rows;
	};

	// Filter dialog content
	const filterContent = (
		<div className={classes.filterContent}>
			{groupFieldsByRow().map((row, rowIndex) => (
				<div key={rowIndex} className={classes.filterRow}>
					{row.map(field => renderFilterField(field))}
				</div>
			))}
		</div>
	);

	// Filter dialog actions
	const filterActions = (
		<div className={classes.buttonContainer}>
			<Box display="flex" gap={2}>
				<ButtonOmni onClick={handleClear} size="small" variant="outlined" label="Clear" />
			</Box>
			<Box display="flex" gap={2}>
				<ButtonOmni onClick={handleCancel} size="small" variant="outlined" label="Cancel" />
				<ButtonOmni onClick={handleApply} size="small" variant="outlined" label="Apply" />
			</Box>
		</div>
	);

	return (
		<>
			<div className={classes.filterSection}>
				<div className={classes.filterControls}>
					{title && (
						<Typography
							style={{
								fontSize: '2rem',
								fontWeight: 600,
								letterSpacing: '-0.01em',
								lineHeight: 1.15,
								whiteSpace: 'nowrap',
								color: theme.palette.text.primary
							}}
						>
							{title}
						</Typography>
					)}
					{showDateRange && (
						<div className={classes.datePickerContainer}>
							<DateRangePickerOmni startDate={startDate} endDate={endDate} onDateChange={onDateChange} />
						</div>
					)}
				</div>

				{/* Middle Content Section */}
				{middleContent && <div className={classes.middleSection}>{middleContent}</div>}

				<div className={classes.rightSection}>
					{/* Refresh Button  */}
					{showRefresh && (
						<CachedIcon
							onClick={onRefresh || handleApply}
							sx={{ cursor: 'pointer', color: theme.palette.secondary.value, fontSize: 28 }}
						/>
					)}
					{/* Custom Right Content */}
					{customRightContent}

					{/* Filter Button */}
					{hasFilters && (
						<Badge badgeContent={getActiveFilterCount()} color="primary">
							<ButtonOmniNew
								onClick={() => setFilterDialogOpen(true)}
								buttonType="filters"
								label="Filters"
								variant="outlined"
							/>
						</Badge>
					)}

					{/* Export Button */}
					{showExport && (
						<ButtonOmniNew
							loading={isExporting}
							buttonType="export"
							label={isExporting ? 'Exporting...' : 'Export'}
							onClick={onExport}
							variant="outlined"
							disabled={isExporting}
						/>
					)}
				</div>
			</div>

			{/* Filter Dialog */}
			{hasFilters && (
				<ModalOmni
					open={filterDialogOpen}
					handleClose={handleCancel}
					title="Filters"
					content={filterContent}
					actions={filterActions}
					customWidth={600}
				/>
			)}
		</>
	);
}

export default DateFilterWithExport;
